import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { WP_FAQ_CATEGORIES } from "@/content/work-permit-corpus/faq-defs";

export const Route = createFileRoute("/work-permit/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/work-permit-corpus");
    return { faqs: m.WP_FAQ_TH };
  },
  component: WpFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    const title = `ใบอนุญาตทำงานคนต่างชาติ — FAQ ${faqs.length} ข้อ อ้างอิงราชการ`;
    const description =
      "คำถามที่พบบ่อยเรื่องใบอนุญาตทำงานของคนต่างชาติในไทย — เงื่อนไขนายจ้าง ทุนจดทะเบียน อัตราส่วนลูกจ้างไทย แบบฟอร์มและขั้นตอนที่กรมการจัดหางาน วีซ่า Non-B การต่ออายุ BOI One Stop Service และบทลงโทษ อ้างอิง doe.go.th และ immigration.go.th";
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: "/work-permit/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/work-permit/faq" }],
        keywords: [
          "ใบอนุญาตทำงาน",
          "work permit ไทย",
          "วีซ่า Non-B",
          "ทุนจดทะเบียน 2 ล้าน",
          "กรมการจัดหางาน",
          "ต่ออายุใบอนุญาตทำงาน",
        ],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "ใบอนุญาตทำงาน FAQ", url: `${site}/work-permit/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/work-permit/faq")) },
      ],
    };
  },
});

function WpFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <span>ใบอนุญาตทำงาน FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          ใบอนุญาตทำงานคนต่างชาติในไทย — คำถามที่พบบ่อย {faqs.length} ข้อ
        </h1>
        <p className="mb-6 text-muted-foreground">
          คำตอบทุกข้ออ้างอิงพระราชกำหนดการบริหารจัดการการทำงานของคนต่างด้าว พ.ศ. 2560 และที่แก้ไขเพิ่มเติม
          (ฉบับที่ 2) พ.ศ. 2561 พร้อมประกาศของกรมการจัดหางาน (doe.go.th) สำนักงานตรวจคนเข้าเมือง
          (immigration.go.th) BOI (boi.go.th) สำนักงานประกันสังคม (sso.go.th) และกรมสรรพากร (rd.go.th)
        </p>
        <div className="mb-8 rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
          <strong className="text-foreground">ข้อควรทราบ:</strong> ใบอนุญาตทำงานผูกกับนายจ้าง ตำแหน่งงาน
          และสถานที่ทำงานที่ระบุไว้ การอนุมัติทุกกรณีเป็นดุลพินิจของพนักงานเจ้าหน้าที่ เราไม่รับประกันผลการพิจารณา
          ค่าธรรมเนียมและกรอบเวลาเป็นค่าตามประกาศซึ่งอาจเปลี่ยนแปลงได้ โปรดตรวจสอบกับหน่วยงานก่อนดำเนินการ
        </div>

        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>English:</strong>{" "}
          <a href="/en/work-permit/faq" className="text-primary underline">
            Thailand work permit FAQ →
          </a>
        </div>

        <section aria-label="หมวดคำถามใบอนุญาตทำงาน" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">เลือกดูตามหมวด</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {WP_FAQ_CATEGORIES.map((c) => (
              <a
                key={c.slug}
                href={`/work-permit/faq/${c.slug}`}
                className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm"
              >
                <div className="mb-1 font-semibold">{c.titleTh}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descTh}</div>
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="mr-2 text-muted-foreground">Q{i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <section className="mt-12">
          <h2 className="mb-3 text-xl font-bold">บริการที่เกี่ยวข้อง</h2>
          <ul className="grid gap-2 text-sm md:grid-cols-2">
            <li><a className="text-primary underline" href="/visa/thai/non-b">วีซ่าธุรกิจ Non-B</a></li>
            <li><a className="text-primary underline" href="/visa/faq/work-permit">FAQ วีซ่าทำงานและใบอนุญาตทำงาน</a></li>
            <li><a className="text-primary underline" href="/translation/faq">แปลและรับรองวุฒิการศึกษา</a></li>
            <li><a className="text-primary underline" href="/police-clearance">หนังสือรับรองความประพฤติ</a></li>
          </ul>
        </section>

        <section className="mt-12 rounded-lg border bg-primary/5 p-6">
          <h2 className="mb-2 text-xl font-bold">ยังหาคำตอบไม่เจอ?</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            สอบถามคุณสมบัตินายจ้างและค่าบริการของกรณีคุณกับทีมงานได้ทางโทรศัพท์ LINE หรืออีเมล — จ.-ส. 9:00-18:00
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 font-medium text-primary-foreground">
            ติดต่อทีมงาน →
          </a>
        </section>

        <FaqSourceNote topic="work-permit" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}